import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, X } from 'lucide-react';
import { useWorkspace } from '../../../../domains/workspace/state/useWorkspace';
import { useAppContext } from '../../../../context/AppContext';
import { createDocument as apiCreateDocument } from '../../../../domains/document/api/documentApi';
import { resolveDocumentId, openResume, openCoverLetter } from '../../../../utils/navigationGuard';

export default function CreateMenu({
  isCollapsed,
  theme,
  onCreateVariant,
  onImportResume,
  onClearChat,
  externalStep,
  onExternalClose,
}) {
  const navigate = useNavigate();
  const { createWorkspace } = useWorkspace();
  const {
    switchDocument,
    mergeDocumentMetadata,
    fetchDocuments,
    activeWorkspace,
    setActiveProgress,
    setIsCreatingDocument,
    setEntryIntent,
    user,
  } = useAppContext();

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [docType, setDocType] = useState('resume'); // 'resume' | 'cover_letter'
  
  // Steps for Resume Wizard: 'select' | 'job_details' | 'templates'
  const [step, setStep] = useState('select'); 

  // Fields for Resume
  const [resumeName, setResumeName] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('harvard_classic');
  
  // Fields for Cover Letter
  const [coverName, setCoverName] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isDark = theme === 'dark';
  const textPrimary = isDark ? '#f4f4f5' : '#0f172a';
  const textSecondary = isDark ? '#9ca3af' : '#4b5563';

  // Sync externally triggered modals
  useEffect(() => {
    if (externalStep) {
      if (externalStep === 'cover_letter') {
        setDocType('cover_letter');
      } else {
        setDocType('resume');
        setStep(externalStep);
      }
      setModalOpen(true);
    }
  }, [externalStep]);

  const resetForms = () => {
    setStep('select');
    setDocType('resume');
    setResumeName('');
    setTargetRole('');
    setCoverName('');
    setCompany('');
    setJobTitle('');
    setError('');
  };

  const handleClose = () => {
    setModalOpen(false);
    onExternalClose?.();
    resetForms();
  };

  const executeCreateResume = async (templateId, isBuildWithAI = false) => {
    const defaultName = !isBuildWithAI && targetRole.trim() 
      ? `${targetRole.trim()} Resume` 
      : `${user?.name ? `${user.name.split(' ')[0]}'s` : 'My'} Resume`;
    const finalName = isBuildWithAI ? defaultName : (resumeName.trim() || defaultName);
    setLoading(true);
    setError('');
    setIsCreatingDocument(true);

    setActiveProgress({
      title: 'Creating Resume',
      statusText: 'Creating your workspace...',
      percent: 15,
    });

    try {
      // Step 1: Create workspace
      const newWs = await createWorkspace({ name: `Workspace - ${finalName}` });

      // Step 2: Create document
      const result = await apiCreateDocument(newWs.id, 'resume', finalName, { 
        target_role: isBuildWithAI ? "" : targetRole.trim(),
        template_id: templateId || selectedTemplate
      });

      const docId = resolveDocumentId(result);

      // Step 3: Sync metadata locally
      const newDocMeta = {
        id: docId,
        type: 'resume',
        name: finalName,
        isMaster: false,
        updatedAt: new Date().toISOString(),
        ...(result?.metadata || {}),
      };
      mergeDocumentMetadata(newDocMeta);

      // Signal backend: this is a brand-new resume (entry_intent = new_resume)
      if (setEntryIntent) setEntryIntent('new_resume');

      // Step 4: switch document & bootstrap conversation session
      await switchDocument({ type: 'resume', id: docId });

      if (isBuildWithAI) {
        openResume(navigate, docId, 'chat', { state: { source: 'build_with_ai', docId } });
      } else {
        const role = targetRole.trim();
        const coachingPrompt = role
          ? `I just created a new resume for a ${role} role using the template ${templateId}. Please introduce yourself as my Resume Coach, then ask me 3 quick questions to get started.`
          : `I just created a new blank resume using the template ${templateId}. Please introduce yourself as my Resume Coach and ask me about my background.`;

        openResume(navigate, docId, 'chat', { state: { source: 'create', docId, coachingPrompt } });
      }

      setActiveProgress(p => p && { ...p, statusText: 'Ready!', percent: 100 });
      setTimeout(() => setActiveProgress(null), 600);

      fetchDocuments(activeWorkspace?.id || null).catch(() => {});
      handleClose();
    } catch (err) {
      console.error('[CreateMenu] executeCreateResume error:', err);
      setError(err.message || 'Failed to create resume');
      setActiveProgress({
        title: 'Creation Failed',
        error: err.message || 'Failed to create resume.',
        onClose: () => setActiveProgress(null),
      });
    } finally {
      setLoading(false);
      setIsCreatingDocument(false);
    }
  };

  const handleCreateCoverLetter = async (e) => {
    e.preventDefault();
    if (!coverName.trim()) {
      setError('Cover Letter Title is required');
      return;
    }
    setLoading(true);
    setError('');
    setIsCreatingDocument(true);

    setActiveProgress({
      title: 'Creating Cover Letter',
      statusText: 'Creating your workspace...',
      percent: 15,
    });

    try {
      const newWs = await createWorkspace({ name: `Workspace - ${coverName.trim()}` });
      const result = await apiCreateDocument(newWs.id, 'cover_letter', coverName.trim(), {
        job_title: jobTitle.trim(),
        company: company.trim()
      });

      const docId = resolveDocumentId(result);

      const newDocMeta = {
        id: docId,
        type: 'cover_letter',
        name: coverName.trim(),
        isMaster: false,
        updatedAt: new Date().toISOString(),
        ...(result?.metadata || {}),
      };
      mergeDocumentMetadata(newDocMeta);

      await switchDocument({ type: 'cover_letter', id: docId });

      const coachingPrompt = `I just created a new cover letter${company.trim() ? ` for ${company.trim()}` : ''}${jobTitle.trim() ? ` for the ${jobTitle.trim()} role` : ''}. Please introduce yourself as my Cover Letter Coach and help me write it.`;
      openCoverLetter(navigate, docId, { state: { source: 'create', docId, coachingPrompt } });

      setActiveProgress(p => p && { ...p, statusText: 'Ready!', percent: 100 });
      setTimeout(() => setActiveProgress(null), 600);

      fetchDocuments(activeWorkspace?.id || null).catch(() => {});
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to create cover letter');
      setActiveProgress({
        title: 'Creation Failed',
        error: err.message || 'Failed to create cover letter.',
        onClose: () => setActiveProgress(null),
      });
    } finally {
      setLoading(false);
      setIsCreatingDocument(false);
    }
  };

  return (
    <>

      {/* Onboarding Wizard Modal */}
      {modalOpen && (
        <div 
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)',
            padding: '16px',
          }}
        >
          <div 
            onClick={e => e.stopPropagation()}
            style={{
              background: isDark ? '#121214' : '#FFFFFF',
              borderRadius: '24px',
              border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
              width: '100%',
              maxWidth: '680px',
              maxHeight: 'calc(100vh - 40px)',
              overflowY: 'auto',
              boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
              padding: isMobile ? '20px' : '32px',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxSizing: 'border-box'
            }}
          >
            {/* Modal Close Button */}
            <button 
              onClick={handleClose} 
              style={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                background: isDark ? '#1F1F24' : '#F3F4F6',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isDark ? '#a1a1aa' : '#4b5563',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <X size={16} />
            </button>

            {/* Top Logo and Step Progress bar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <img
                  src="/resumebar-icon.svg"
                  alt="Resumebar Icon"
                  style={{ width: '22px', height: '22px', objectFit: 'contain' }}
                />
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: '15px', fontWeight: 800, color: isDark ? '#ffffff' : '#14213D' }}>
                  resumebar.in
                </span>
              </div>

              {/* Progress Line removed for new conversational onboarding */}
            </div>

            {/* DocType Selector (Toggle at top for Resume/Cover Letter) */}
            {step === 'select' && (
              <div style={{ display: 'flex', background: isDark ? '#1F1F24' : '#F3F4F6', borderRadius: '10px', padding: '4px', alignSelf: 'center', marginBottom: '24px' }}>
                <button 
                  onClick={() => setDocType('resume')}
                  style={{
                    padding: '6px 16px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    background: docType === 'resume' ? (isDark ? '#27272a' : '#FFFFFF') : 'transparent',
                    color: docType === 'resume' ? 'var(--brand)' : (isDark ? '#a1a1aa' : '#4b5563')
                  }}
                >
                  Resume
                </button>
                <button 
                  onClick={() => { setDocType('cover_letter'); setStep('select'); }}
                  style={{
                    padding: '6px 16px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    background: docType === 'cover_letter' ? (isDark ? '#27272a' : '#FFFFFF') : 'transparent',
                    color: docType === 'cover_letter' ? 'var(--brand)' : (isDark ? '#a1a1aa' : '#4b5563')
                  }}
                >
                  Cover Letter
                </button>
              </div>
            )}

            {/* Main Form Content */}
            {docType === 'resume' ? (
              /* RESUME WIZARD STEPS */
              <>
                {step === 'select' && (
                  <div>
                    <h2 style={{
                      fontSize: '18px', fontWeight: 800, textAlign: 'center', margin: '0 0 24px',
                      color: isDark ? '#ffffff' : '#0f172a', fontFamily: "'Outfit', sans-serif"
                    }}>
                      👋 Hey, How would you like to start your resume?
                    </h2>

                    {/* 2-Card Layout */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '10px' : '16px' }}>
                      {/* Card 1: Upload Resume */}
                      <button
                        onClick={() => { onImportResume?.(true); handleClose(); }}
                        style={{ ...startCardStyle(isDark, isMobile), borderStyle: 'dashed' }}
                        onMouseEnter={e => cardHover(e, isDark)}
                        onMouseLeave={e => cardLeave(e, isDark)}
                      >
                        <div style={cardIconStyle('rgba(14, 165, 233, 0.1)', '#0284c7')}>
                          <Upload size={18} />
                        </div>
                        <h3 style={cardTitleStyle(isDark)}>Upload Resume</h3>
                        <p style={cardDescStyle(isDark)}>Select or Drag and drop your current resume</p>
                      </button>

                      {/* Card 2: Build with AI */}
                      <button 
                        onClick={() => executeCreateResume('harvard_classic', true)}
                        style={startCardStyle(isDark, isMobile)}
                        onMouseEnter={e => cardHover(e, isDark)}
                        onMouseLeave={e => cardLeave(e, isDark)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <div style={cardIconStyle('rgba(124, 58, 237, 0.1)', 'var(--brand)')}>
                            <FileText size={18} />
                          </div>
                          <span style={{ fontSize: '8px', fontWeight: 800, padding: '2px 6px', borderRadius: '4px', background: 'rgba(124, 58, 237, 0.1)', color: 'var(--brand)' }}>
                            AI Assistance
                          </span>
                        </div>
                        <h3 style={cardTitleStyle(isDark)}>Build with AI</h3>
                        <p style={cardDescStyle(isDark)}>Let AI guide you to build a resume from scratch</p>
                      </button>
                    </div>
                  </div>
                )}

                {step === 'job_details' && (
                  <div>
                    <h2 style={{
                      fontSize: '18px', fontWeight: 800, margin: '0 0 20px',
                      color: isDark ? '#ffffff' : '#0f172a', fontFamily: "'Outfit', sans-serif"
                    }}>
                      Tell us about the job you are targeting!
                    </h2>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={labelStyle(isDark)}>Target Role / Job Title *</label>
                        <input
                          type="text"
                          value={targetRole}
                          onChange={(e) => {
                            const val = e.target.value;
                            setTargetRole(val);
                            setResumeName(val.trim() ? `${val.trim()} Resume` : '');
                          }}
                          placeholder="e.g. Software Engineer"
                          style={inputStyle(isDark)}
                          required
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                        <button
                          onClick={() => setStep('select')}
                          style={backBtnStyle(isDark)}
                        >
                          Back
                        </button>
                        <button
                          onClick={() => {
                            if (!targetRole.trim()) {
                              setError('Target Role / Job Title is required');
                              return;
                            }
                            setError('');
                            setStep('templates');
                          }}
                          style={nextBtnStyle}
                        >
                          Next
                        </button>
                      </div>
                      
                      {error && (
                        <div style={{ fontSize: '12px', color: '#ef4444', textAlign: 'center' }}>{error}</div>
                      )}
                    </div>
                  </div>
                )}

                {step === 'templates' && (
                  <div>
                    <h2 style={{
                      fontSize: '18px', fontWeight: 800, margin: '0 0 8px',
                      color: isDark ? '#ffffff' : '#0f172a', fontFamily: "'Outfit', sans-serif"
                    }}>
                      🎨 Choose a template to get started
                    </h2>
                    <p style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#64748b', margin: '0 0 20px' }}>
                      Select a layout. You can always change it later in a click.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '10px' : '16px' }}>
                      {[
                        { id: 'harvard_classic', name: 'Harvard Classic', desc: 'ATS Safe Standard' },
                        { id: 'creative_portfolio', name: 'Creative Portfolio', desc: 'Clean Two-Column' },
                        { id: 'modern_ats_pro', name: 'Modern ATS Pro', desc: 'Optimized Fresh look' }
                      ].map(tmpl => (
                        <button
                          key={tmpl.id}
                          onClick={() => executeCreateResume(tmpl.id)}
                          style={{
                            ...startCardStyle(isDark, isMobile),
                            padding: '16px',
                            borderColor: selectedTemplate === tmpl.id ? 'var(--brand)' : (isDark ? '#27272a' : '#E5E7EB'),
                            background: selectedTemplate === tmpl.id ? (isDark ? '#27272a' : 'rgba(124, 58, 237, 0.04)') : (isDark ? '#1F1F24' : '#FFFFFF')
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; }}
                          onMouseLeave={e => { if (selectedTemplate !== tmpl.id) e.currentTarget.style.borderColor = isDark ? '#27272a' : '#E5E7EB'; }}
                        >
                          <div style={{
                            width: '100%', height: '110px', background: isDark ? '#121214' : '#F9FAFB',
                            borderRadius: '8px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`
                          }}>
                            <FileText size={28} style={{ opacity: 0.3 }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: textPrimary }}>{tmpl.name}</span>
                          <span style={{ fontSize: '9px', color: isDark ? '#71717a' : '#94a3b8', marginTop: '2px' }}>{tmpl.desc}</span>
                        </button>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                      <button onClick={() => setStep('job_details')} style={backBtnStyle(isDark)}>
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* COVER LETTER FORM */
              <form onSubmit={handleCreateCoverLetter} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h2 style={{
                  fontSize: '18px', fontWeight: 800, margin: '0 0 10px',
                  color: isDark ? '#ffffff' : '#0f172a', fontFamily: "'Outfit', sans-serif"
                }}>
                  Create New Cover Letter
                </h2>

                <div>
                  <label style={labelStyle(isDark)}>Cover Letter Title *</label>
                  <input
                    type="text"
                    value={coverName}
                    onChange={(e) => setCoverName(e.target.value)}
                    placeholder="e.g. Google PM Application"
                    style={inputStyle(isDark)}
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle(isDark)}>Target Company *</label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="e.g. Google"
                    style={inputStyle(isDark)}
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle(isDark)}>Target Job Title *</label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g. Product Manager"
                    style={inputStyle(isDark)}
                    required
                  />
                </div>

                {error && (
                  <div style={{ fontSize: '12px', color: '#ef4444', textAlign: 'center' }}>{error}</div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button type="button" onClick={handleClose} style={backBtnStyle(isDark)}>
                    Cancel
                  </button>
                  <button type="submit" style={nextBtnStyle}>
                    Create Letter
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/* Card Redesign Styling Helpers */
const startCardStyle = (isDark, isMobile) => ({
  background: isDark ? '#1F1F24' : '#FFFFFF',
  border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
  borderRadius: '16px',
  padding: isMobile ? '16px' : '24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  width: '100%',
  boxSizing: 'border-box'
});

const cardHover = (e, isDark) => {
  e.currentTarget.style.borderColor = 'var(--brand)';
  e.currentTarget.style.transform = 'translateY(-2px)';
  e.currentTarget.style.boxShadow = isDark 
    ? '0 12px 24px rgba(0,0,0,0.4)' 
    : '0 12px 24px rgba(14, 165, 233, 0.05)';
};

const cardLeave = (e, isDark) => {
  e.currentTarget.style.borderColor = isDark ? '#27272a' : '#E5E7EB';
  e.currentTarget.style.transform = 'translateY(0)';
  e.currentTarget.style.boxShadow = 'none';
};

const cardIconStyle = (bg, color) => ({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  background: bg,
  color: color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '4px'
});

const cardTitleStyle = (isDark) => ({
  fontSize: '13.5px',
  fontWeight: 700,
  margin: '4px 0 0',
  color: isDark ? '#ffffff' : '#0f172a'
});

const cardDescStyle = (isDark) => ({
  fontSize: '11px',
  lineHeight: 1.45,
  margin: 0,
  color: isDark ? '#9ca3af' : '#64748b'
});

const labelStyle = (isDark) => ({
  fontSize: '11px',
  fontWeight: 700,
  color: isDark ? '#9ca3af' : '#4b5563',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'block',
  marginBottom: '6px'
});

const inputStyle = (isDark) => ({
  width: '100%',
  background: isDark ? '#1F1F24' : '#F9FAFB',
  border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
  borderRadius: '8px',
  color: isDark ? '#f4f4f5' : '#0f172a',
  fontSize: '13px',
  padding: '10px 14px',
  outline: 'none',
  boxSizing: 'border-box'
});

const backBtnStyle = (isDark) => ({
  flex: 1,
  padding: '10px',
  borderRadius: '8px',
  border: `1px solid ${isDark ? '#27272a' : '#E5E7EB'}`,
  background: 'transparent',
  color: isDark ? '#a1a1aa' : '#4b5563',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer'
});

const nextBtnStyle = {
  flex: 1.5,
  padding: '10px',
  borderRadius: '8px',
  border: 'none',
  background: 'var(--brand)',
  color: '#ffffff',
  fontSize: '12.5px',
  fontWeight: 700,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(14, 165, 233, 0.25)'
};
